import { useState, type FormEvent } from 'react';
import { useCreateVendor, useDeleteVendor, useVendors } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Plus, Trash2, ExternalLink, Phone, Mail } from 'lucide-react';
import PullToRefresh from '@/components/PullToRefresh';
import { useQueryClient } from '@tanstack/react-query';

export default function Vendors() {
  const q = useVendors();
  const create = useCreateVendor();
  const del = useDeleteVendor();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  return (
    <PullToRefresh onRefresh={() => queryClient.invalidateQueries({ queryKey: ['vendors'] })}>
      <div className="space-y-8">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-2">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              Boundaries · Vendors
            </p>
            <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
              Vendors
            </h1>
            <p className="text-muted-foreground max-w-2xl">
              Plumber, electrician, tree service, mower mechanic. The work that's *not*
              homeowner-scope.
            </p>
          </div>
          <Button onClick={() => setCreating(true)} className="h-11 self-start sm:self-auto" data-testid="new-vendor">
            <Plus className="h-4 w-4 mr-2" aria-hidden /> New vendor
          </Button>
        </header>

        {q.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
        {q.data && q.data.length === 0 ? (
          <p className="text-sm text-muted-foreground">No vendors yet.</p>
        ) : null}
        {q.data && q.data.length > 0 ? (
          <ul className="divide-y divide-rule rounded-md border border-rule bg-card" data-testid="vendor-list">
            {q.data.map((v) => (
              <li
                key={v.id}
                className="flex items-start gap-3 px-4 py-3"
                data-testid={`vendor-row-${v.id}`}
              >
                <div className="flex-1 min-w-0 space-y-0.5">
                  {v.trade ? (
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      {v.trade}
                    </p>
                  ) : null}
                  <p className="font-display text-base font-medium leading-tight">{v.name}</p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground pt-1">
                    {v.phone ? (
                      <a href={`tel:${v.phone}`} className="inline-flex items-center gap-1 hover:text-foreground">
                        <Phone className="h-3 w-3" aria-hidden /> {v.phone}
                      </a>
                    ) : null}
                    {v.email ? (
                      <a href={`mailto:${v.email}`} className="inline-flex items-center gap-1 hover:text-foreground">
                        <Mail className="h-3 w-3" aria-hidden /> {v.email}
                      </a>
                    ) : null}
                    {v.website ? (
                      <a
                        href={v.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 hover:text-foreground"
                      >
                        <ExternalLink className="h-3 w-3" aria-hidden /> Site
                      </a>
                    ) : null}
                  </div>
                  {v.notes ? <p className="text-sm text-muted-foreground pt-1">{v.notes}</p> : null}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:text-destructive"
                  onClick={() => del.mutateAsync(v.id)}
                  aria-label="Delete vendor"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        ) : null}

        <Sheet open={creating} onOpenChange={setCreating}>
          <SheetContent side="bottom">
            <SheetHeader>
              <SheetTitle>New vendor</SheetTitle>
            </SheetHeader>
            <VendorForm
              onSubmit={async (body) => {
                await create.mutateAsync(body);
                setCreating(false);
              }}
              onCancel={() => setCreating(false)}
              pending={create.isPending}
            />
          </SheetContent>
        </Sheet>
      </div>
    </PullToRefresh>
  );
}

function VendorForm({
  onSubmit,
  onCancel,
  pending,
}: {
  onSubmit: (body: {
    name: string;
    trade: string | null;
    phone: string | null;
    email: string | null;
    website: string | null;
    notes: string | null;
  }) => Promise<void>;
  onCancel: () => void;
  pending: boolean;
}) {
  const [name, setName] = useState('');
  const [trade, setTrade] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name required.');
      return;
    }
    try {
      await onSubmit({
        name: name.trim(),
        trade: trade.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        website: website.trim() || null,
        notes: notes.trim() || null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4 pt-4 pb-2" data-testid="vendor-form">
      <Field label="Name" required>
        <Input value={name} onChange={(e) => setName(e.target.value)} required autoFocus className="h-11" data-testid="vendor-name" />
      </Field>
      <Field label="Trade">
        <Input value={trade} onChange={(e) => setTrade(e.target.value)} placeholder="Plumber / Electrician / …" className="h-11" />
      </Field>
      <Field label="Phone">
        <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-11" />
      </Field>
      <Field label="Email">
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" />
      </Field>
      <Field label="Website">
        <Input type="url" value={website} onChange={(e) => setWebsite(e.target.value)} className="h-11" />
      </Field>
      <Field label="Notes">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ring resize-y"
        />
      </Field>
      {error ? <p className="text-sm text-warning">{error}</p> : null}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={pending} data-testid="vendor-submit">
          {pending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="font-mono text-[11px] uppercase tracking-wider">
        {label}
        {required ? ' *' : ''}
      </Label>
      {children}
    </div>
  );
}
